import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CrudList, CrudStyles } from '../shared/Crud';
import routeService from '../../services/routeService';
import { ROUTES } from '../../routes';
import { formatDateTimeBR } from '../../utils/dateTimeDisplay';
import { EyeIcon, PlusIcon } from '../Layout/icons';
import { ActionBtn } from '../Deliveries/DeliveriesList.styles';
import RouteStatusBadge from './RouteStatusBadge';
import {
    DesktopOnly,
    MobileOnly,
    RouteListCard,
    RouteListCardActions,
    RouteListCardHeader,
    RouteListCardMeta,
    RouteListCardRow,
    RouteListCardTitle,
    RouteListCards,
} from './RotasPage.styles';

const { Button, Container, Header, PaginationContainer, PageInfo, PageButtons } = CrudStyles;

export default function RoutesListPage() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        page: 0,
        totalPages: 0,
        first: true,
        last: true,
    });
    const [sorting, setSorting] = useState({ field: 'creationDateTime', direction: 'desc' });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const result = await routeService.list({
                    page: pagination.page,
                    size: 10,
                    sort: `${sorting.field},${sorting.direction}`,
                });
                if (cancelled) return;
                setData(result.content || []);
                const totalPages = result.page?.totalPages ?? 0;
                const pageNumber = Math.min(
                    result.page?.number ?? 0,
                    Math.max(0, totalPages - 1),
                );
                setPagination({
                    page: pageNumber,
                    totalPages,
                    first: totalPages === 0 || pageNumber === 0,
                    last: totalPages === 0 || pageNumber >= totalPages - 1,
                });
            } catch {
                if (!cancelled) setData([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [pagination.page, sorting.field, sorting.direction]);

    const handlePageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage }));
    };

    const handleSort = (field, direction) => {
        setSorting({ field, direction });
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    const columns = [
        { header: 'ID', accessor: 'id', sortable: true },
        {
            header: 'Nome',
            accessor: 'alias',
            sortable: false,
            render: (item) => item.alias || '—',
        },
        {
            header: 'Motorista',
            accessor: 'driverName',
            sortable: false,
            render: (item) => item.driverName || '—',
        },
        {
            header: 'Pontos',
            accessor: 'pointCount',
            sortable: true,
            render: (item) => item.pointCount ?? '—',
        },
        {
            header: 'Status',
            accessor: 'state',
            sortable: false,
            render: (item) => <RouteStatusBadge state={item.state} />,
        },
        {
            header: 'Criado em',
            accessor: 'creationDateTime',
            sortable: true,
            render: (item) => formatDateTimeBR(item.creationDateTime),
        },
    ];

    const handleDetails = (e, item) => {
        e.stopPropagation();
        navigate(`${ROUTES.ROTAS}/${item.id}`);
    };

    const renderActions = (item) => (
        <ActionBtn type="button" onClick={(e) => handleDetails(e, item)} title="Detalhes">
            <EyeIcon />
            Detalhes
        </ActionBtn>
    );

    const paginationBlock = (
        <PaginationContainer>
            <PageInfo>
                Página {pagination.totalPages === 0 ? 0 : pagination.page + 1} de {pagination.totalPages || 0}
            </PageInfo>
            <PageButtons>
                <Button
                    variant="outline"
                    disabled={pagination.totalPages === 0 || pagination.first}
                    onClick={() => handlePageChange(pagination.page - 1)}
                >
                    Anterior
                </Button>
                <Button
                    variant="outline"
                    disabled={pagination.totalPages === 0 || pagination.last}
                    onClick={() => handlePageChange(pagination.page + 1)}
                >
                    Próxima
                </Button>
            </PageButtons>
        </PaginationContainer>
    );

    return (
        <>
            <DesktopOnly>
                <CrudList
                    title="Rotas"
                    onNew={() => navigate(ROUTES.ROTAS_NOVA)}
                    newButtonLabel="Nova rota"
                    columns={columns}
                    data={data}
                    loading={loading}
                    pagination={{
                        ...pagination,
                        onPageChange: handlePageChange,
                    }}
                    sorting={{
                        ...sorting,
                        onSort: handleSort,
                    }}
                    actions={renderActions}
                    tableMinWidth="720px"
                />
            </DesktopOnly>

            <MobileOnly>
                <Container>
                    <Header>
                        <h1>Rotas</h1>
                        <Button variant="primary" onClick={() => navigate(ROUTES.ROTAS_NOVA)}>
                            <PlusIcon />
                            Nova rota
                        </Button>
                    </Header>

                    {loading ? (
                        <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>Carregando...</p>
                    ) : data.length === 0 ? (
                        <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>Nenhum registro encontrado</p>
                    ) : (
                        <RouteListCards>
                            {data.map((item) => (
                                <RouteListCard key={item.id}>
                                    <RouteListCardHeader>
                                        <RouteListCardTitle>
                                            <strong title={item.alias || `Roteiro #${item.id}`}>
                                                {item.alias || `Roteiro #${item.id}`}
                                            </strong>
                                            <span>ID {item.id}</span>
                                        </RouteListCardTitle>
                                        <RouteStatusBadge state={item.state} />
                                    </RouteListCardHeader>
                                    <RouteListCardMeta>
                                        <RouteListCardRow>
                                            <span>Motorista</span>
                                            <span>{item.driverName || '—'}</span>
                                        </RouteListCardRow>
                                        <RouteListCardRow>
                                            <span>Pontos</span>
                                            <span>{item.pointCount ?? '—'}</span>
                                        </RouteListCardRow>
                                        <RouteListCardRow>
                                            <span>Criado em</span>
                                            <span>{formatDateTimeBR(item.creationDateTime)}</span>
                                        </RouteListCardRow>
                                    </RouteListCardMeta>
                                    <RouteListCardActions>
                                        {renderActions(item)}
                                    </RouteListCardActions>
                                </RouteListCard>
                            ))}
                        </RouteListCards>
                    )}

                    {paginationBlock}
                </Container>
            </MobileOnly>
        </>
    );
}
